import { useCallback } from 'react';
import opConfig from 'openpathConfig';

import { featureCodes } from 'constants/featureCodes';
import { getAccessToken } from 'utils/accessToken';
import { checkAuthIsUPCPartnerOrg } from 'utils/checkAuthIsUPCPartnerOrg';
import { useFeatureFlag } from 'utils/customHooks/useFeatureFlag';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { NativeAppStatus, WEAR_NATIVE_APP_ID } from 'utils/nativeApps';

/**
 * Returns a click handler for the Wear button in the local app switcher.
 *
 * - If the org has an active Wear org native app, opens its `determinedUrl`.
 * - Otherwise opens the global Wear native app's upsell url (partner or
 *   customer variant). Partner detection is equivalent to the package's
 *   partner detection in `@msi-alta/alta-app-switcher`, plus the
 *   erpParentCenter/erpParentStore scopes via `checkAuthIsUPCPartnerOrg`.
 *
 * Which url each environment points at is Helium data, so nothing env-specific
 * lives here. The url is opened as-is (no `/o/{orgId}` suffix) because Wear is
 * not an Alta app with org-scoped routes.
 *
 * While any query the decision depends on is still loading the click is a
 * no-op: sending a licensed org to the upsell page, or a
 * non-licensed org to the app, would both be wrong, and the button visibility
 * does not depend on these queries, so there is no loading flicker. The
 * global native apps, org features and access token queries only pick the
 * upsell url, so they are waited on only when the org has no active Wear app.
 *
 * A failed org native apps request is also a no-op rather than an upsell: an
 * unknown license state is not a negative one. useOpQuery already shows the
 * error message for it.
 */
export const useNavigateToWear = (orgId: number) => {
  const token = getAccessToken();

  // Already cached by AppSwitcherMenu; gates the global native apps request
  const { value: isWearEnabled } = useFeatureFlag<boolean>(
    'IS_WEAR_ENABLED',
    orgId,
  );

  const {
    data: wearOrgNativeApp,
    isPending: isOrgNativeAppsPending,
    isError: isOrgNativeAppsError,
  } = useOpQuery({
    apiEndpointName: 'listOrgNativeApps',
    parameters: [orgId],
    select: (res) =>
      (res.json.data ?? []).find(
        (orgNativeApp) =>
          orgNativeApp.nativeAppId === WEAR_NATIVE_APP_ID &&
          orgNativeApp.status === NativeAppStatus.Active,
      ),
  });

  const { data: wearNativeApp, isPending: isNativeAppsPending } = useOpQuery({
    apiEndpointName: 'listGlobalNativeApps',
    enabled: Boolean(isWearEnabled),
    suppressErrorMessage: true,
    select: (res) =>
      (res.json.data ?? []).find(
        (nativeApp) => nativeApp.id === WEAR_NATIVE_APP_ID,
      ),
    // Global native apps do not change during a session
    gcTime: Infinity,
    staleTime: Infinity,
  });

  // 19 = subOrgs and 81 = subOrgsVideo
  const { data: hasPartnerFeatures = false, isPending: isOrgFeaturesPending } =
    useOpQuery({
      apiEndpointName: 'listOrgFeatures',
      parameters: [Number(orgId)],
      select: (res) =>
        (res.json.data ?? []).some(({ id }) =>
          [featureCodes.subOrgs, featureCodes.subOrgsVideo].includes(id),
        ),
    });

  const {
    data: isUnifiedPartnerIdentity = false,
    isPending: isAccessTokenPending,
  } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [token],
    enabled: Boolean(token),
    select: (res) =>
      Boolean(
        res.json.data?.tokenScopeList?.some(
          (item) => item.org?.isUnified && checkAuthIsUPCPartnerOrg(item),
        ),
      ),
  });

  const isPartner = isUnifiedPartnerIdentity || hasPartnerFeatures;

  const handleNavigateToWear = useCallback(() => {
    if (isOrgNativeAppsPending || isOrgNativeAppsError) {
      return;
    }

    if (wearOrgNativeApp) {
      if (wearOrgNativeApp.determinedUrl) {
        window.open(
          wearOrgNativeApp.determinedUrl,
          '_self',
          'noopener,noreferrer',
        );
      }
      return;
    }

    if (
      isNativeAppsPending ||
      isOrgFeaturesPending ||
      // A disabled query (no token) stays pending forever, so only wait when
      // there is a token to describe
      (Boolean(token) && isAccessTokenPending)
    ) {
      return;
    }

    const upsellUrl = isPartner
      ? wearNativeApp?.partnerUpsellUrl
      : wearNativeApp?.customerUpsellUrl;

    // Mirrors handleUpsellClick in @msi-alta/alta-app-switcher
    const params: Record<string, string | number> = {
      orgId,
      region: opConfig.APP_SWITCHER_REGION_OVERRIDE || opConfig.ENV,
    };
    const finalUpsellUrl = Object.entries(params).reduce(
      (mappedUrl, [key, value]) => mappedUrl.replace(`:${key}`, String(value)),
      upsellUrl || '',
    );

    if (!finalUpsellUrl) {
      return;
    }

    window.open(finalUpsellUrl, '_self', 'noopener,noreferrer');
  }, [
    isAccessTokenPending,
    isNativeAppsPending,
    isOrgFeaturesPending,
    isOrgNativeAppsError,
    isOrgNativeAppsPending,
    isPartner,
    orgId,
    wearNativeApp?.customerUpsellUrl,
    wearNativeApp?.partnerUpsellUrl,
    token,
    wearOrgNativeApp,
  ]);

  return {
    handleNavigateToWear,
  };
};
